/**
 * TopExposureCard — the hero panel above the KPI strip on S11.
 *
 * Picks the single biggest open balance — the oldest overdue invoice
 * the SPA can see (already sorted descending by `created_at`, but we
 * pick by `dueOn` ascending here so the row that's been overdue
 * longest wins). Renders:
 *
 *   - Eyebrow pill: "Top exposure · N days overdue"
 *   - Headline: "<Customer> owes ₹X" (lakh/crore formatted)
 *   - Subtext: doc number · issued · due · "the single biggest open
 *     balance" framing
 *   - Timeline bar: visual age strip (Issued → Due → Today markers)
 *   - Right-side Quick actions panel: Send WhatsApp reminder · Call
 *     decision maker · Open invoice
 *
 * All copy is deterministic (no AI) — the "% reduction" line is
 * computed from outstanding total. The "Acme pays N days late on
 * average" tip is deferred to V1.x (needs receipts + payment-behavior
 * tracking) and is skipped today.
 *
 * If there's no overdue invoice the card doesn't render — the parent
 * checks `pickTopExposure` returns null and skips the panel.
 *
 * @see prototype/sales/invoices.html — hero feature panel
 * @see 10-sales-invoicing-spec.md §12 — list/register behaviour
 */
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, ExternalLink, MessageCircle, Phone } from "lucide-react";
import { Button, Card, CardBody } from "@flint/ui";
import { formatLakhCrore } from "../lib/format-money.ts";

export interface TopExposure {
  invoiceId: string;
  docNumber: string | null;
  customerId: string;
  customerName: string;
  customerPhone: string | null;
  totalPaise: string;
  issuedOn: string;
  dueOn: string;
  /** Days past `dueOn` (positive). */
  daysOverdue: number;
  /** Total overdue across the tenant (string paise) — used for the
   *  "clearing this cuts X% of overdue" framing. */
  totalOverduePaise: string;
}

export interface TopExposureCardProps {
  exposure: TopExposure;
  onSendReminder: () => void;
  /** True while the WhatsApp send mutation is running. */
  reminderPending?: boolean;
  reminderJustSent?: boolean;
}

/**
 * Pick the row that should headline the hero. Returns null when the
 * tenant has no overdue invoices — the parent should skip the card
 * in that case (no need to draft a "no exposure" empty state).
 */
export function pickTopExposure(
  rows: ReadonlyArray<{
    id: string;
    docNumber: string | null;
    customerId: string;
    customerName: string;
    customerPhone: string | null;
    totalPaise: string;
    issuedOn: string;
    dueOn: string;
    status: string;
  }>,
  totalOverduePaise: string,
): TopExposure | null {
  const overdueRows = rows
    .filter((r) => r.status === "overdue")
    .slice() // copy so we don't mutate the parent's array
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  const top = overdueRows[0];
  if (!top) return null;
  return {
    invoiceId: top.id,
    docNumber: top.docNumber,
    customerId: top.customerId,
    customerName: top.customerName,
    customerPhone: top.customerPhone,
    totalPaise: top.totalPaise,
    issuedOn: top.issuedOn,
    dueOn: top.dueOn,
    daysOverdue: daysBetween(top.dueOn, todayIsoUtc()),
    totalOverduePaise,
  };
}

export function TopExposureCard({
  exposure,
  onSendReminder,
  reminderPending,
  reminderJustSent,
}: TopExposureCardProps) {
  const cutPercent = computeCutPercent(exposure.totalPaise, exposure.totalOverduePaise);
  const totalSpan = computeTotalSpan(exposure.issuedOn, exposure.dueOn);

  return (
    <Card>
      <CardBody className="p-0 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-4">
          {/* Left — exposure summary + timeline */}
          <div className="p-5 sm:p-6 flex flex-col gap-4 border-b lg:col-span-3 lg:border-b-0 lg:border-r border-line">
            <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-rose-200 bg-rose-50 text-rose-700 px-2.5 py-1 text-sm font-medium">
              <AlertTriangle size={12} aria-hidden />
              Top exposure · {exposure.daysOverdue} days overdue
            </span>

            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-ink">
                {exposure.customerName} owes{" "}
                <span className="text-rose-700">{formatLakhCrore(exposure.totalPaise)}</span>
              </h2>
              <p className="mt-1 text-sm text-muted">
                <span className="font-mono text-ink-2">{exposure.docNumber ?? "— pending —"}</span>{" "}
                · issued {exposure.issuedOn} · due {exposure.dueOn}
                {cutPercent !== null
                  ? ` · the single biggest open balance. Clearing it cuts your overdue total by ${cutPercent}%.`
                  : null}
              </p>
            </div>

            {/* Timeline */}
            <div className="mt-1">
              <div className="relative h-1.5 rounded-full bg-line">
                {/* Aging fill — from 0% to 100% representing issued-to-today. */}
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-rose-500"
                  style={{ width: "100%" }}
                />
                {/* Due marker */}
                {totalSpan.duePercent !== null ? (
                  <div
                    role="img"
                    className="absolute -top-1 h-3.5 w-3.5 rounded-full bg-amber-500 border-2 border-bg"
                    style={{ left: `calc(${totalSpan.duePercent}% - 7px)` }}
                    aria-label="Due date"
                  />
                ) : null}
                {/* Today marker */}
                <div
                  role="img"
                  className="absolute -top-1 right-0 h-3.5 w-3.5 rounded-full bg-rose-700 border-2 border-bg"
                  style={{ transform: "translateX(7px)" }}
                  aria-label="Today"
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-sm text-muted">
                <span>Issued {exposure.issuedOn}</span>
                {totalSpan.duePercent !== null ? <span>Due {exposure.dueOn}</span> : null}
                <span className="text-rose-700 font-medium">
                  Today · {exposure.daysOverdue} days
                </span>
              </div>
            </div>
          </div>

          {/* Right — quick actions */}
          <div className="p-5 sm:p-6 bg-raised flex flex-col gap-2">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted">
              Quick actions
            </p>
            {reminderJustSent ? (
              <p className="text-sm text-emerald-700">Reminder queued via WhatsApp.</p>
            ) : null}
            <Button
              type="button"
              intent="primary"
              size="sm"
              onClick={onSendReminder}
              disabled={!exposure.customerPhone || reminderPending}
              title={!exposure.customerPhone ? "Customer has no phone on file" : undefined}
              className="w-full justify-start"
            >
              <MessageCircle size={14} />
              <span>{reminderPending ? "Sending…" : "Send WhatsApp reminder"}</span>
            </Button>
            {exposure.customerPhone ? (
              <Button asChild intent="ghost" size="sm" className="w-full justify-start">
                <a href={`tel:+91${exposure.customerPhone}`}>
                  <Phone size={14} />
                  <span>Call decision maker</span>
                </a>
              </Button>
            ) : null}
            <Button asChild intent="ghost" size="sm" className="w-full justify-start">
              <Link to="/sales/invoices/$invoiceId" params={{ invoiceId: exposure.invoiceId }}>
                <ExternalLink size={14} />
                <span>Open invoice</span>
                <ArrowRight size={12} className="ml-auto opacity-60" />
              </Link>
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ── helpers ────────────────────────────────────────────────────────

/** What % of the tenant's overdue total this single invoice represents. */
function computeCutPercent(rowPaise: string, totalOverduePaise: string): number | null {
  try {
    const row = BigInt(rowPaise);
    const total = BigInt(totalOverduePaise);
    if (total === 0n) return null;
    // Round to whole percent. (row * 100) / total — bigint integer divide.
    return Number((row * 100n) / total);
  } catch {
    return null;
  }
}

/** Where the due-date marker sits on the timeline (0..100). */
function computeTotalSpan(issuedOn: string, dueOn: string): { duePercent: number | null } {
  const issued = parseIso(issuedOn);
  const due = parseIso(dueOn);
  const today = new Date();
  if (!issued || !due || today.getTime() <= issued.getTime()) {
    return { duePercent: null };
  }
  const total = today.getTime() - issued.getTime();
  const dueOffset = due.getTime() - issued.getTime();
  const pct = Math.max(0, Math.min(100, Math.round((dueOffset * 100) / total)));
  return { duePercent: pct };
}

function parseIso(iso: string): Date | null {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isFinite(d.getTime()) ? d : null;
}

function daysBetween(fromIso: string, toIso: string): number {
  const a = parseIso(fromIso);
  const b = parseIso(toIso);
  if (!a || !b) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)));
}

function todayIsoUtc(): string {
  return new Date().toISOString().slice(0, 10);
}
